import { chain } from "lodash";
import { useEffect, useMemo } from "react";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import { useAssetStore } from "legacyNext/store/asset-page-store";
import { gramsToKg } from "utils/report.helpers";

export const useAssetIntensity = (assetId: string, key: ChartDisplayType) => {
    const { fetchIntensity, intensities, pathways, loading } = useAssetStore();

    useEffect(() => {
        if (assetId) fetchIntensity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetId]);

    const crremUnit =
        key === ChartDisplayType.EMISSIONS ? "kgCO2/m²/yr" : "kWh/m²/yr";

    const chartData = useMemo(() => {
        if (!intensities) return [];

        const intensityKey =
            key === ChartDisplayType.EMISSIONS
                ? "emissionIntensity"
                : "energyIntensity";
        const projectedKey =
            key === ChartDisplayType.EMISSIONS
                ? "projectedEmissionIntensity"
                : "projectedEnergyIntensity";

        return chain(intensities)
            .sortBy("year")
            .map((item) => {
                const value = item[intensityKey] ?? null;
                const projectedValue = item[projectedKey] ?? null;

                const pathwaysValues = Object.fromEntries(
                    pathways
                        .filter(
                            (pathway) =>
                                pathway.year === item.year &&
                                pathway.unit === crremUnit
                        )
                        .map((pathway) => [
                            `crrem-${pathway.crremCategory.toLowerCase()}`,
                            pathway.value,
                        ])
                );

                return {
                    date: new Date(item.year.toString()),
                    int:
                        value && key === ChartDisplayType.EMISSIONS
                            ? gramsToKg(value)
                            : value,
                    proj:
                        projectedValue && key === ChartDisplayType.EMISSIONS
                            ? gramsToKg(projectedValue)
                            : projectedValue,
                    ...pathwaysValues,
                };
            })
            .value();
    }, [intensities, key, pathways, crremUnit]);

    return {
        pathways: pathways.filter((p) => p.unit === crremUnit),
        data: chartData,
        loading,
    };
};
